"use client";

import React from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pricingPlans = [{
  title: "The Essential",
  price: "$1,750",
  description: "Perfect for a quick confidence boost and introductory experience.",
  features: ["1 Hour Studio Session", "2 Outfit Changes", "Professional Hair & Makeup", "10 Retouched Digital Images", "Private Online Gallery"],
  ctaText: "Book Essential",
  ctaLink: "#quote-form",
  isPopular: false
}, {
  title: "The Signature",
  price: "$2,100",
  description: "Our most comprehensive luxury boudoir experience.",
  features: ["2 Hour Studio Session", "4 Outfit Changes", "Professional Hair & Makeup", "25 Retouched Digital Images", "Little Black Book Album", "Same-Day Image Reveal"],
  ctaText: "Book Signature",
  ctaLink: "#quote-form",
  isPopular: true
}, {
  title: "The VIP Experience",
  price: "$2,750",
  description: "The ultimate indulgence on location at a luxury hotel or home.",
  features: ["4 Hour On-Location Session", "Unlimited Outfit Changes", "Professional Hair & Makeup", "All Retouched Digital Images", "Luxury Leather Album", "Champagne & Refreshments"],
  ctaText: "Book VIP",
  ctaLink: "#quote-form",
  isPopular: false
}];
export default function Pricing() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="pricing" className="bg-background py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl md:text-5xl font-bold uppercase tracking-widest text-primary mb-6">
            Investment
          </h2>
          <p className="font-serif italic text-lg md:text-xl text-foreground/80 leading-relaxed">
            Experience the luxury of XO Boudoir. Our curated collections are designed to provide an unforgettable, empowering session with stunning heirloom artwork.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {pricingPlans.map((plan, i) => <div data-index={i} key={i} className="flex h-full">
              <Card className="flex flex-col w-full rounded-none border-border shadow-sm hover:shadow-md transition-shadow duration-300 bg-background">
                <CardHeader className="text-center pb-8 pt-10">
                  {plan.isPopular && <div className="text-destructive text-xs font-bold uppercase tracking-widest mb-3">
                      Most Booked
                    </div>}
                  <CardTitle className="font-sans font-bold uppercase text-xl text-primary tracking-wide mb-4">
                    {plan.title}
                  </CardTitle>
                  <div className="font-sans font-bold text-5xl text-primary mb-4">
                    {plan.price}
                  </div>
                  <CardDescription className="font-sans text-foreground/70 text-sm px-4">
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="flex-grow px-8">
                  <ul className="space-y-5">
                    {plan.features.map((feature, featureIndex) => <li data-index={featureIndex} key={featureIndex} className="flex items-start">
                        <Check className="h-5 w-5 text-primary shrink-0 mr-3 mt-0.5" />
                        <span className="font-sans text-sm text-foreground/90 leading-tight">
                          {feature}
                        </span>
                      </li>)}
                  </ul>
                </CardContent>
                
                <CardFooter className="pt-8 pb-10 px-8">
                  <Button asChild className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-sans font-bold uppercase tracking-wider py-6">
                    <Link to={plan.ctaLink}>{plan.ctaText}</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}